import { get, post, put, del } from '@/api/apiService.ts';
import type { User } from '@/types/auth.ts';
import type { CreateSalespersonRequest } from '@/types/team-management.ts';

export interface TeamResponse {
  id: string;
  name: string;
  createdAt: string;
  members: User[];
}

export interface CreateTeamRequest {
  name: string;
}

export interface UpdateTeamRequest {
  name: string;
}

export interface AddTeamMemberRequest {
  userId: string;
}

const teamService = {
  // ── Teams ──────────────────────────────────────────────────────────

  getMyTeams(): Promise<TeamResponse[]> {
    return get<TeamResponse[]>('/api/team');
  },

  getTeamById(teamId: string): Promise<TeamResponse> {
    return get<TeamResponse>(`/api/team/${teamId}`);
  },

  createTeam(data: CreateTeamRequest): Promise<TeamResponse> {
    return post<CreateTeamRequest, TeamResponse>('/api/team', data);
  },

  updateTeam(teamId: string, data: UpdateTeamRequest): Promise<TeamResponse> {
    return put<UpdateTeamRequest, TeamResponse>(`/api/team/${teamId}`, data);
  },

  deleteTeam(teamId: string): Promise<void> {
    return del<void>(`/api/team/${teamId}`);
  },

  // ── Members ────────────────────────────────────────────────────────

  addMember(teamId: string, userId: string): Promise<User> {
    return post<AddTeamMemberRequest, User>(`/api/team/${teamId}/members`, { userId });
  },

  removeMember(teamId: string, userId: string): Promise<void> {
    return del<void>(`/api/team/${teamId}/members/${userId}`);
  },

  // ── Salespeople (company-wide) ─────────────────────────────────────

  getSalespeople(): Promise<User[]> {
    return get<User[]>('/api/team/salespeople');
  },

  createSalesperson(data: CreateSalespersonRequest): Promise<User> {
    return post<CreateSalespersonRequest, User>('/api/team/salespeople', data);
  },

  deleteSalesperson(id: string): Promise<void> {
    return del<void>(`/api/team/salespeople/${id}`);
  },
};

export default teamService;
